import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Save as SaveIcon, Cancel as CancelIcon } from "@mui/icons-material";

// Clinical Note Type choices (should match Django model)
const CLINICAL_NOTE_TYPES = [
  { value: 1, label: "Progress Note" },
  { value: 2, label: "Consultation Note" },
  { value: 3, label: "Procedure Note" },
  { value: 4, label: "Discharge Note" },
  { value: 5, label: "Transfer Note" },
  { value: 6, label: "Other" },
];

export default function ClinicalNoteCreate({ open, onClose, encounter }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0); // 0 = Simple, 1 = SOAP

  const [formData, setFormData] = useState({
    encounter: encounter?.id || null,
    note_type: 1, // Default to Progress Note
    content: "",
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const createNoteMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/clinical-notes/`,
        data,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["clinical-notes", "encounter", encounter?.id]);
      queryClient.invalidateQueries(["encounters", encounter?.id]);
      queryClient.invalidateQueries(["encounters"]);
      // Reset form
      setFormData({
        encounter: encounter?.id || null,
        note_type: 1,
        content: "",
        subjective: "",
        objective: "",
        assessment: "",
        plan: "",
      });
      setFormErrors({});
      setTabValue(0);
      onClose();
    },
    onError: (err) => {
      console.error("Error creating clinical note:", err);
      const errorMsg = err.response?.data?.detail || "Failed to create clinical note";
      setFormErrors({ general: errorMsg });
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let errors = {};
    
    if (!formData.encounter) {
      errors.encounter = t("validation.encounterRequired", { defaultValue: "Encounter is required" });
    }
    
    if (tabValue === 0) {
      // Simple note - content is required
      if (!formData.content.trim()) {
        errors.content = t("validation.contentRequired", { defaultValue: "Note content is required" });
      }
    } else {
      // SOAP format - at least one field should be filled
      if (!formData.subjective?.trim() && !formData.objective?.trim() && 
          !formData.assessment?.trim() && !formData.plan?.trim()) {
        errors.general = t("validation.soapRequired", { defaultValue: "At least one SOAP field is required" });
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Prepare data based on tab
    const submitData = {
      encounter: formData.encounter,
      note_type: formData.note_type,
    };

    if (tabValue === 0) {
      // Simple note
      submitData.content = formData.content;
    } else {
      // SOAP format
      submitData.subjective = formData.subjective || null;
      submitData.objective = formData.objective || null;
      submitData.assessment = formData.assessment || null;
      submitData.plan = formData.plan || null;
      // Combine SOAP fields into content for display
      submitData.content = [
        formData.subjective && `S: ${formData.subjective}`,
        formData.objective && `O: ${formData.objective}`,
        formData.assessment && `A: ${formData.assessment}`,
        formData.plan && `P: ${formData.plan}`,
      ].filter(Boolean).join("\n\n");
    }

    createNoteMutation.mutate(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {t("clinicalNotes.addNote", { defaultValue: "Add Clinical Note" })}
        </DialogTitle>
        <DialogContent>
          {createNoteMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.general || t("error.genericCreate")}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("clinicalNotes.encounter", { defaultValue: "Encounter" })}
                value={encounter ? `Encounter ${encounter.id} - ${encounter.encounter_type_display || "N/A"}` : ""}
                InputProps={{ readOnly: true }}
                variant="outlined"
                size="small"
                error={!!formErrors.encounter}
                helperText={formErrors.encounter}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={!!formErrors.note_type}>
                <InputLabel>{t("clinicalNotes.noteType", { defaultValue: "Note Type" })}</InputLabel>
                <Select
                  name="note_type"
                  value={formData.note_type}
                  label={t("clinicalNotes.noteType", { defaultValue: "Note Type" })}
                  onChange={handleChange}
                >
                  {CLINICAL_NOTE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {t(`clinicalNotes.types.${type.label.toLowerCase().replace(/\s/g, '')}`, type.label)}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.note_type && (
                  <Typography color="error" variant="caption">
                    {formErrors.note_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label={t("clinicalNotes.simpleNote", { defaultValue: "Simple Note" })} />
                <Tab label={t("clinicalNotes.soapFormat", { defaultValue: "SOAP Format" })} />
              </Tabs>
            </Grid>

            {tabValue === 0 ? (
              // Simple note
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("clinicalNotes.content", { defaultValue: "Note Content" })}
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  multiline
                  rows={8}
                  variant="outlined"
                  required
                  error={!!formErrors.content}
                  helperText={formErrors.content}
                  placeholder={t("clinicalNotes.contentPlaceholder", { defaultValue: "Enter your clinical note here..." })}
                />
              </Grid>
            ) : (
              // SOAP format
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("clinicalNotes.subjective", { defaultValue: "Subjective (S)" })}
                    name="subjective"
                    value={formData.subjective}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={t("clinicalNotes.subjectivePlaceholder", { defaultValue: "Patient's complaints and symptoms" })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("clinicalNotes.objective", { defaultValue: "Objective (O)" })}
                    name="objective"
                    value={formData.objective}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={t("clinicalNotes.objectivePlaceholder", { defaultValue: "Physical examination findings" })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("clinicalNotes.assessment", { defaultValue: "Assessment (A)" })}
                    name="assessment"
                    value={formData.assessment}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={t("clinicalNotes.assessmentPlaceholder", { defaultValue: "Clinical assessment and diagnosis" })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("clinicalNotes.plan", { defaultValue: "Plan (P)" })}
                    name="plan"
                    value={formData.plan}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={t("clinicalNotes.planPlaceholder", { defaultValue: "Treatment plan and management" })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            startIcon={<CancelIcon />}
            disabled={createNoteMutation.isPending}
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={createNoteMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={createNoteMutation.isPending}
          >
            {createNoteMutation.isPending
              ? t("common.creating", { defaultValue: "Creating..." })
              : t("common.create", { defaultValue: "Create Note" })}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
